import './JornadaPage.css';
import { useJornada } from '../hooks/useJornada';

// ── SVG icons ─────────────────────────────────────────────
const ArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const PlayFill = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
);
const DocIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);
const CheckSmall = () => (
  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const CircleIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const LockSmall = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

// depth class por ordem
function depthClass(ordem) {
  if (ordem <= 2) return 'depth-1';
  if (ordem === 3) return 'depth-3';
  if (ordem <= 6) return 'depth-4';
  return 'depth-6';
}

function EncontroStep({ encontro, isLast }) {
  const locked    = encontro.status === 'locked';
  const completed = encontro.status === 'completed';
  const current   = encontro.current === true;
  const ordem     = encontro.ordem;
  const dc        = depthClass(ordem);

  const stepClass = [
    'ocean-step',
    completed ? 'completed' : '',
    current   ? 'current'   : '',
    locked    ? 'locked'    : '',
    dc,
    isLast    ? 'last'      : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={stepClass}>
      <div className="step-number">
        {ordem}
        {completed && <div className="check"><CheckSmall /></div>}
      </div>

      <div className="lesson-card">
        <div className="lesson-header">
          <span className="lesson-tag">
            Encontro {ordem}{current ? ' · Agora' : ''}
          </span>
        </div>
        <h2>{encontro.titulo}</h2>

        {!locked && (
          <div className="lesson-actions">
            {encontro.hubla_url ? (
              <a href={encontro.hubla_url} target="_blank" rel="noopener noreferrer">
                <PlayFill /> Acessar aulas
              </a>
            ) : (
              <>
                <button><DocIcon /> Reflexão escrita</button>
                <button><CircleIcon /> Hábitos da semana</button>
              </>
            )}
          </div>
        )}

        {locked && (
          <div className="lock-hint">
            <LockSmall /> Aguardando liberação
          </div>
        )}
      </div>
    </div>
  );
}

function BonusCard({ bonus }) {
  if (bonus.status !== 'available') return null;
  return (
    <div className="bonus-card">
      <span className="bonus-tag">Aula bônus</span>
      <h3>{bonus.titulo}</h3>
      {bonus.tipo && <p>{bonus.tipo === 'gravada' ? 'Aula gravada' : 'Aula ao vivo'}</p>}
      {bonus.hubla_url && (
        <a href={bonus.hubla_url} target="_blank" rel="noopener noreferrer">
          <PlayFill /> Acessar aula
        </a>
      )}
    </div>
  );
}

export default function JornadaPage({ onBack }) {
  const { jornada, encontros, loading } = useJornada();

  const regulares  = encontros.filter(e => e.tipo !== 'bonus');
  const bonus      = encontros.filter(e => e.tipo === 'bonus' && e.status === 'available');
  const concluidos = regulares.filter(e => e.status === 'completed').length;
  const reflexoes  = concluidos; // placeholder — futuramente do banco
  const habitos    = concluidos * 6; // placeholder
  const progPct    = regulares.length ? (concluidos / regulares.length) * 100 : 0;

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="loading-spinner" /></div>;
  }

  return (
    <div className="jornada-page">
      {/* Header */}
      <div className="oc-header">
        <div className="oc-back-row">
          <button className="oc-back" onClick={onBack}>
            <ArrowLeft /> voltar
          </button>
          {jornada?.hubla_area_url && (
            <a href={jornada.hubla_area_url} target="_blank" rel="noopener noreferrer" className="oc-hubla">
              <PlayFill /> Acessar aulas
            </a>
          )}
        </div>
        <div className="oc-title">Jornada ao Oceano</div>
        <div className="oc-sub">Uma jornada de conexão, profundidade e transformação.</div>
      </div>

      {/* Minha Evolução */}
      <div className="evolucao">
        <div className="ev-label">✦ Minha Evolução</div>
        <div className="ev-stats">
          <div className="ev-stat">
            <span className="ev-num">{concluidos}</span>
            <span className="ev-lbl">encontros</span>
          </div>
          <div className="ev-stat">
            <span className="ev-num">{reflexoes}</span>
            <span className="ev-lbl">reflexões</span>
          </div>
          <div className="ev-stat">
            <span className="ev-num">{habitos}</span>
            <span className="ev-lbl">hábitos</span>
          </div>
        </div>
        <div className="depth-track">
          <div className="depth-fill" style={{ width: `${progPct}%` }} />
        </div>
        <div className="depth-txt">{concluidos * 5}m de profundidade alcançada</div>
      </div>

      {/* Encontros */}
      <div className="steps">
        {bonus.map(b => <BonusCard key={b.id} bonus={b} />)}
        {regulares.map((e, i) => (
          <EncontroStep key={e.id} encontro={e} isLast={i === regulares.length - 1} />
        ))}
      </div>

      <div style={{ height: 60 }} />
    </div>
  );
}
